import { useListAddons, useListClusters } from 'api/admin';
import { useListAddonInstancesInfinite } from 'api/admin-infinite';
import { AddonInstance } from 'api/admin-models';
import { FilterableTable } from 'components/tables/filterable-table';
import { formatISO } from 'date-fns';
import { useSplitPanel } from 'contexts/split-panel';
import { AddonInstanceDetails } from './addons_details';

interface TableData extends AddonInstance {
  cluster_name?: string;
  addon_name?: string;
  primary_component_name?: string;
}

export function AccountAddonInstances(props: {
  orgId: string;
  accountId: string;
}): JSX.Element {
  const updateSplitPanel = useSplitPanel();
  const { data } = useListAddonInstancesInfinite(props.accountId);
  const { data: clusterData } = useListClusters(props.accountId);
  const { data: addonData } = useListAddons({ page_size: 1000 });

  const allAddonInstances = data?.flatMap((page) => page.data ?? []);

  const tableData: TableData[] | undefined = allAddonInstances?.map((item) => ({
    cluster_name: clusterData?.data?.find((c) => c.id === item.cluster_id)
      ?.name,
    addon_name: addonData?.data?.find((c) => c.id === item.addon_id)?.name,
    primary_component_name: item.primary_component?.image?.registry_url,

    ...item,
  }));

  return (
    <FilterableTable<TableData>
      selectionMode="single"
      data={tableData ? tableData : undefined}
      columns={[
        {
          id: 'id',
          label: 'Instance ID',
          cell: (item) => item.id || '',

          defaultVisible: true,
          sortingField: 'id',
          filterOperators: ['=', '!=', ':', '!:'],
          filterOptions: Array.from(
            new Set(
              tableData
                ?.filter((c) => c.id)
                .map((c) => c.id || ''),
            ),
          ),
        },
        {
          id: 'cluster_name',
          label: 'Cluster Name',
          cell: (item) => item.cluster_name || '',

          defaultVisible: true,
          sortingField: 'cluster_name',
          filterOperators: ['=', '!=', ':', '!:'],
          filterOptions: Array.from(
            new Set(
              tableData
                ?.filter((c) => c.cluster_name)
                .map((c) => c.cluster_name || ''),
            ),
          ),
        },
        {
          id: 'cluster_id',
          label: 'Cluster ID',
          cell: (item) => item.cluster_id || '-',

          defaultVisible: true,
          sortingField: 'cluster_id',
          filterOperators: ['=', '!='],
          filterOptions: Array.from(
            new Set(
              tableData
                ?.filter((c) => c.cluster_id)
                .map((c) => c.cluster_id || ''),
            ),
          ),
        },
        {
          id: 'addon_name',
          label: 'Name',
          cell: (item) => item.addon_name || item.addon_id || '-',

          defaultVisible: true,

          sortingField: 'addon_name',
          filterOperators: ['=', '!=', ':', '!:'],
          filterOptions: Array.from(
            new Set(
              tableData
                ?.filter((c) => c.addon_name)
                .map((c) => c.addon_name || ''),
            ),
          ),
        },
        {
          id: 'primary_component_image',
          label: 'Primary Component Image',
          cell: (item) => item.primary_component?.image?.registry_url || '-',

          defaultVisible: true,

          sortingField: 'primary_component_image',
          filterOperators: ['=', '!=', ':', '!:'],
        },
        {
          id: 'primary_component_tag',
          label: 'Primary Component Tag',
          cell: (item) => item.primary_component?.image?.tag || '-',
          defaultVisible: true,
          sortingField: 'primary_component_tag',
          filterOperators: ['=', '!=', ':', '!:'],
        },
        {
          id: 'version',
          label: 'Version',
          cell: (item) => item.version || '-',

          defaultVisible: true,

          sortingField: 'version',
          filterOperators: ['=', '!=', ':', '!:'],
        },
        {
          id: 'eol_date',
          label: 'EOL',
          cell: (item) =>
            item.eol_date === undefined
              ? '-'
              : formatISO(new Date(item.eol_date * 1000), {
                  representation: 'date',
                }),

          defaultVisible: true,

          sortingField: 'eol_date',
          filterOperators: ['=', '!=', '>', '<', '>=', '<='],
        },
      ]}
      selectionTrackBy={(item) => `${item.cluster_id}#${item.id}`}
      onSelectionChange={(event) => {
        updateSplitPanel(
          <AddonInstanceDetails
            addonInstance={{
              accountId: props.accountId,
              addonInstanceId: event.detail.selectedItems[0].id,
            }}
          />,
        );
      }}
    />
  );
}
